import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, Card, EmptyState, Field } from "../components/ui-ext";
import StatusPill from "../components/StatusPill";
import PhotoUpload from "../components/PhotoUpload";
import { Plus, X, Loader2, Trash2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import ExportButton from "../components/ExportButton";

export default function ExpensesPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const preTaskId = params.get("task");
  const [items, setItems] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [show, setShow] = useState(!!preTaskId);
  const [filter, setFilter] = useState("");

  const load = () => api.get("/expenses", { params: { status: filter || undefined } }).then((r) => setItems(r.data));

  useEffect(() => {
    load();
    api.get("/tasks").then((r) => setTasks(r.data));
  }, []); // eslint-disable-line
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  return (
    <div>
      <PageHeader
        eyebrow="Expenses"
        title={user.role === "engineer" ? "My Expenses" : "Team Expenses"}
        subtitle="Travel, food, lodging, extra costs — with bill photos & approval trail."
        right={
          <div className="flex items-center gap-2">
            {user.role !== "engineer" && <ExportButton endpoint="expenses.xlsx" testid="export-expenses-btn" />}
            <button data-testid="new-expense-btn" onClick={() => setShow(true)} className="bg-[#FF5722] hover:bg-[#F4511E] px-4 py-2 text-sm rounded-sm flex items-center gap-2"><Plus className="w-4 h-4" /> Submit</button>
          </div>
        }
      />

      <div className="flex gap-3 mb-6">
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="bg-[#0B0C10] border border-white/10 px-4 py-2 text-sm rounded-sm" data-testid="exp-filter">
          <option value="">All statuses</option>
          <option value="submitted">Submitted</option>
          <option value="manager_approved">Manager Approved</option>
          <option value="admin_approved">Admin Approved</option>
          <option value="paid">Paid</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      <Card>
        {items.length === 0 ? (
          <EmptyState title="No expenses yet" hint="Submit expense claims against tasks." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full tech-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Category</th>
                  <th>Engineer</th>
                  <th>Journey</th>
                  <th>Amount</th>
                  <th>Approved</th>
                  <th>Status</th>
                  <th>Payment</th>
                </tr>
              </thead>
              <tbody>
                {items.map((e) => (
                  <tr key={e.id} data-testid={`exp-row-${e.id}`}>
                    <td className="mono text-xs">{e.date}</td>
                    <td className="capitalize">{e.category?.replace("_", " ")}</td>
                    <td>{e.engineer_name}</td>
                    <td className="text-xs">
                      {(e.journey_legs || []).length > 0 ? (
                        <div className="space-y-0.5 max-w-[280px]">
                          {e.journey_legs.slice(0, 3).map((l, i) => (
                            <div key={i} className="text-slate-300 truncate">
                              <span className="mono text-[10px] text-[#FF5722]">#{i + 1}</span>{" "}
                              {l.from_point} → {l.to_point}{" "}
                              <span className="mono text-slate-500">({l.mode}{l.distance_km ? `, ${l.distance_km}km` : ""})</span>
                            </div>
                          ))}
                          {e.journey_legs.length > 3 && (
                            <div className="mono text-[10px] text-slate-500">+{e.journey_legs.length - 3} more</div>
                          )}
                        </div>
                      ) : <span className="text-slate-600">—</span>}
                    </td>
                    <td className="mono">₹ {e.amount}</td>
                    <td className="mono">{e.approved_amount != null ? `₹ ${e.approved_amount}` : "—"}</td>
                    <td><StatusPill status={e.status} /></td>
                    <td className="mono text-xs">
                      {e.payment ? (
                        <div>
                          <div className="text-green-400">₹ {e.payment.amount}</div>
                          <div className="text-slate-500">{e.payment.utr}</div>
                        </div>
                      ) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {show && (
        <ExpenseDialog
          tasks={tasks}
          preTaskId={preTaskId}
          onClose={() => setShow(false)}
          onCreated={() => { setShow(false); load(); }}
        />
      )}
    </div>
  );
}

function ExpenseDialog({ tasks, preTaskId, onClose, onCreated }) {
  const [form, setForm] = useState({
    task_id: preTaskId || "",
    category: "travel",
    amount: 0,
    date: new Date().toISOString().slice(0, 10),
    description: "",
    bill_photos: [],
    journey_legs: [],
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Auto-sum total from legs whenever legs change (only when there are legs)
  const legsTotal = useMemo(
    () => (form.journey_legs || []).reduce((s, l) => s + (parseFloat(l.amount) || 0), 0),
    [form.journey_legs]
  );
  const legsKm = useMemo(
    () => (form.journey_legs || []).reduce((s, l) => s + (parseFloat(l.distance_km) || 0), 0),
    [form.journey_legs]
  );

  useEffect(() => {
    if (form.journey_legs.length > 0) {
      setForm((f) => ({ ...f, amount: legsTotal }));
    }
  }, [legsTotal, form.journey_legs.length]);

  const addLeg = () => {
    const last = form.journey_legs[form.journey_legs.length - 1];
    set("journey_legs", [
      ...form.journey_legs,
      { from_point: last?.to_point || "", to_point: "", mode: "bus", distance_km: 0, amount: 0, remarks: "" },
    ]);
  };
  const updateLeg = (idx, patch) => {
    set(
      "journey_legs",
      form.journey_legs.map((l, i) => (i === idx ? { ...l, ...patch } : l))
    );
  };
  const removeLeg = (idx) => set("journey_legs", form.journey_legs.filter((_, i) => i !== idx));

  const submit = async () => {
    if (form.amount <= 0) return toast.error("Enter amount");
    if (form.category === "travel" && form.journey_legs.length > 0) {
      const bad = form.journey_legs.find((l) => !l.from_point.trim() || !l.to_point.trim());
      if (bad) return toast.error("Each leg needs From & To points");
    }
    setBusy(true);
    try {
      await api.post("/expenses", form);
      toast.success("Expense submitted");
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const showLegs = form.category === "travel";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#13151A] border border-white/10 rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-[#13151A] z-10">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#FF5722]">Expense Claim</div>
            <h2 className="text-xl font-semibold">Submit Expense</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          <Field label="Related Task">
            <select className="input" value={form.task_id} onChange={(e) => set("task_id", e.target.value)} data-testid="exp-task">
              <option value="">— None —</option>
              {tasks.map((t) => <option key={t.id} value={t.id}>{t.task_serial} · {t.site_name || t.job_id}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Category">
              <select className="input" value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="exp-category">
                <option value="travel">Travel</option>
                <option value="food">Food</option>
                <option value="lodging">Lodging</option>
                <option value="extra_cost">Extra Cost</option>
                <option value="other">Other</option>
              </select>
            </Field>
            <Field label="Date"><input type="date" className="input" value={form.date} onChange={(e) => set("date", e.target.value)} /></Field>
          </div>

          {/* Journey legs — travel only */}
          {showLegs && (
            <div className="border border-white/10 rounded-sm bg-[#0B0C10] p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#FF5722]">Journey Legs</div>
                  <div className="text-xs text-slate-500 mt-0.5">Add each leg of your travel — break journey supported</div>
                </div>
                <button
                  type="button"
                  onClick={addLeg}
                  data-testid="add-leg-btn"
                  className="border border-[#FF5722] text-[#FF5722] hover:bg-[#FF5722]/10 px-3 py-1.5 text-xs mono uppercase tracking-widest flex items-center gap-1.5"
                >
                  <Plus className="w-3 h-3" /> Add Leg
                </button>
              </div>

              {form.journey_legs.length === 0 ? (
                <div className="text-xs text-slate-500 py-4 text-center border border-dashed border-white/10">
                  No legs yet · click "Add Leg" to build your journey step-by-step
                </div>
              ) : (
                <div className="space-y-2">
                  {form.journey_legs.map((leg, i) => (
                    <div key={i} className="border border-white/10 bg-[#13151A] p-3 rounded-sm" data-testid={`leg-row-${i}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="mono text-[10px] uppercase tracking-widest text-[#FF5722] w-8">#{i + 1}</span>
                        <button
                          type="button"
                          onClick={() => removeLeg(i)}
                          data-testid={`remove-leg-${i}`}
                          className="ml-auto text-slate-500 hover:text-red-400"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-2 items-center">
                        <input
                          className="input"
                          placeholder="From point"
                          value={leg.from_point}
                          onChange={(e) => updateLeg(i, { from_point: e.target.value })}
                          data-testid={`leg-from-${i}`}
                        />
                        <ArrowRight className="w-4 h-4 text-[#FF5722] mx-auto hidden md:block" />
                        <input
                          className="input"
                          placeholder="To point"
                          value={leg.to_point}
                          onChange={(e) => updateLeg(i, { to_point: e.target.value })}
                          data-testid={`leg-to-${i}`}
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        <select
                          className="input"
                          value={leg.mode}
                          onChange={(e) => updateLeg(i, { mode: e.target.value })}
                          data-testid={`leg-mode-${i}`}
                        >
                          <option value="bus">Bus</option>
                          <option value="auto">Auto</option>
                          <option value="taxi">Taxi / Cab</option>
                          <option value="train">Train</option>
                          <option value="flight">Flight</option>
                          <option value="bike">Bike</option>
                          <option value="own_vehicle">Own Vehicle</option>
                          <option value="toll">Toll / Parking</option>
                          <option value="other">Other</option>
                        </select>
                        <input
                          type="number"
                          className="input mono"
                          placeholder="km"
                          value={leg.distance_km || ""}
                          onChange={(e) => updateLeg(i, { distance_km: parseFloat(e.target.value) || 0 })}
                          data-testid={`leg-km-${i}`}
                        />
                        <input
                          type="number"
                          className="input mono"
                          placeholder="₹ Amount"
                          value={leg.amount || ""}
                          onChange={(e) => updateLeg(i, { amount: parseFloat(e.target.value) || 0 })}
                          data-testid={`leg-amount-${i}`}
                        />
                      </div>
                      <input
                        className="input mt-2 text-xs"
                        placeholder="Remarks (optional) — e.g. ticket no., bus route"
                        value={leg.remarks || ""}
                        onChange={(e) => updateLeg(i, { remarks: e.target.value })}
                        data-testid={`leg-remarks-${i}`}
                      />
                    </div>
                  ))}

                  {/* Legs summary */}
                  <div className="flex items-center justify-between pt-2 border-t border-white/10 mt-3">
                    <span className="mono text-[10px] uppercase tracking-widest text-slate-500">
                      {form.journey_legs.length} leg{form.journey_legs.length > 1 ? "s" : ""} · {legsKm.toFixed(1)} km
                    </span>
                    <span className="mono text-lg font-medium text-[#FF5722]" data-testid="legs-total">
                      ₹ {legsTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          <Field label={showLegs && form.journey_legs.length > 0 ? "Total Amount (auto-summed from legs)" : "Amount (₹)"}>
            <input
              type="number"
              className={`input mono ${showLegs && form.journey_legs.length > 0 ? "opacity-60 cursor-not-allowed" : ""}`}
              value={form.amount}
              onChange={(e) => set("amount", parseFloat(e.target.value) || 0)}
              disabled={showLegs && form.journey_legs.length > 0}
              data-testid="exp-amount"
            />
          </Field>
          <Field label="Description"><textarea rows={2} className="input" value={form.description} onChange={(e) => set("description", e.target.value)} /></Field>
          <PhotoUpload
            value={form.bill_photos}
            onChange={(v) => set("bill_photos", v)}
            label="Bill / Receipt Photos"
            testid="exp-bills"
          />
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button onClick={onClose} className="border border-white/15 px-4 py-2 text-sm rounded-sm">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="exp-submit" className="bg-[#FF5722] hover:bg-[#F4511E] px-5 py-2 text-sm rounded-sm flex items-center gap-2 disabled:opacity-50">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Submit
          </button>
        </div>
      </div>
      <style>{`.input { width: 100%; background: #0B0C10; border: 1px solid rgba(255,255,255,0.1); padding: 8px 12px; font-size: 13px; border-radius: 2px; color: white; }`}</style>
    </div>
  );
}
